import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import Image from 'components/Common/Image'
import TextButton from 'components/Luxkit/Button/TextButton'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import InlineGroup from 'components/Common/InlineGroup'
import OfferDescription from '../OfferDescription'
import { LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'

import Heading from 'components/Luxkit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import moment from 'moment'
import SolidPlayIcon from 'components/Luxkit/Icons/solid/SolidPlayIcon'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import { showUserReviewsRating } from 'lib/order/reviewUtils'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import OfferPagePropertyTitle from '../OfferPagePropertyTitle'
import { LANDING_PAGE_AVAILABLE } from 'constants/cruise'
import Clickable from 'components/Common/Clickable'
import { getShareModalConfig } from 'lib/customer/getShareModalConfig'
import Subtitle from 'components/Luxkit/Typography/Subtitle'

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PropertyLogo = styled(Image)`
  display: none;

  ${mediaQueryUp.desktop} {
    margin-left: ${rem(16)};
    display: block;
    height: ${rem(75)};
  }
`

const OfferBadgeTagSection = styled.div`
  > * + * {
    margin-left: ${rem(8)};
  }
`

interface Props {
  offer: App.HotelOffer;
  property: App.OfferProperty;
  cruiseLines: Array<App.CruiseLine>;
  onWatchVideo: () => void;
  shareModalConfig: ReturnType<typeof getShareModalConfig>;
}

function FlashHotelPropertyDetails(props: Props) {
  const {
    offer,
    property,
    cruiseLines,
    onWatchVideo,
  } = props

  const location = [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')
  const isCruise = (offer.holidayTypes ?? []).map(i => i.toLowerCase()).includes('cruises')

  const getItemLink = useCallback((cruiseLine ?: App.CruiseLine) : string => {
    if (!cruiseLine?.name || !cruiseLine.code) return `/search/cruises?cruiseLines=${offer.vendorName}`
    if (LANDING_PAGE_AVAILABLE.includes(cruiseLine.name)) {
      return `/cruise-line/${cruiseLine.name}`
    }

    return `/search/cruises?cruiseLines=${cruiseLine.code}`
  }, [offer.vendorName])

  const cruiseLine = useMemo(() => {
    const cruiseLine = cruiseLines.find(({ name }) => name === offer.vendorName)
    return cruiseLine
  }, [cruiseLines, offer])

  return (
    <VerticalSpacer gap={20}>
      <Header>
        <VerticalSpacer gap={8}>
          <InlineGroup vertical="center" horizontal="between">
            <BodyTextBlock data-testid="offer-location" variant="medium" weight="bold" startIcon={<LineMapMarkerIcon />}>
              {location}
            </BodyTextBlock>
          </InlineGroup>
          <OfferPagePropertyTitle propertyName={property.name} />
          <Subtitle variant="subtitle2" as="h2">{offer.name}</Subtitle>
          {
          isCruise && <Heading variant="heading6">
            Sailing until {moment(offer.travelToDate).format(LONG_MONTH_LONG_YEAR)}
          </Heading>
        }
        </VerticalSpacer>
        {(cruiseLine || offer.vendorName) && property.logoImageId &&
          <Clickable to={getItemLink(cruiseLine)}>
            <PropertyLogo
                dpr={2}
                height={75}
                enhancedFormats={false}
                id={property.logoImageId}
                alt={`${property.name || 'Vendor'} logo`}
              />
          </Clickable>}
        {(!cruiseLine && !offer.vendorName) && property.logoImageId &&
          <PropertyLogo
            dpr={2}
            height={75}
            enhancedFormats={false}
            id={property.logoImageId}
            alt={`${property.name || 'Vendor'} logo`}
          />}
      </Header>
      {(!!offer.badge || offer.bundledWithFlightsOnly) && <OfferBadgeTagSection>
        {offer.badge && <OfferBadgeTag badge={offer.badge} />}
      </OfferBadgeTagSection>}

      {showUserReviewsRating(property.rating, property.reviewsTotal) && (
        <NumberRating
          link
          inlineLabel
          variant="small"
          total={property.reviewsTotal}
          rating={property.rating}
          ratingSubtitle={property.reviewsSource === 'google' ? 'From online reviews' : undefined}
        />
      )}
      <VerticalSpacer gap={20}>
        {offer.vimeoVideoId &&
          <TextButton kind="secondary" startIcon={<SolidPlayIcon />} onClick={onWatchVideo}>
            Play video
          </TextButton>
        }
        {offer.description && <OfferDescription offer={offer} description={offer.description} />}
      </VerticalSpacer>
    </VerticalSpacer>
  )
}

export default FlashHotelPropertyDetails
